import { RecurrenceType } from "../models/RecurrenceType";

/** A t-function shape compatible with react-i18next without importing its types here. */
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface RecurrenceSummaryInput {
  recurrenceType: RecurrenceType;
  recurrenceDays?: string[];
  recurrenceDaysNumber?: number;
  recurrenceDateEnd?: Date;
  finishAfterOccurrences?: number;
}

const dayLabel = (t: Translate, day: string): string => t(`sessionForm.weekday${day}`);

const dayList = (t: Translate, days?: string[]): string =>
  (days ?? []).map((d) => dayLabel(t, d)).join(", ");

/**
 * A plain-language confirmation of the configured recurrence, e.g.
 * "Repeats every 2 weeks on Tue, Thu · ends after 10 occurrences".
 * Null for one-off sessions.
 */
export function buildRecurrenceSummary(
  input: RecurrenceSummaryInput,
  t: Translate
): string | null {
  const days = dayList(t, input.recurrenceDays);

  let pattern: string;
  switch (input.recurrenceType) {
    case RecurrenceType.No:
      return null;
    case RecurrenceType.Daily:
      pattern = t("sessionForm.summary.daily");
      break;
    case RecurrenceType.DailyWorkdays:
      pattern = t("sessionForm.summary.workdays");
      break;
    case RecurrenceType.DailyWeekend:
      pattern = t("sessionForm.summary.weekend");
      break;
    case RecurrenceType.Weekly:
      pattern = t("sessionForm.summary.weekly", { days });
      break;
    case RecurrenceType.BiWeekly:
      pattern = t("sessionForm.summary.everyNWeeks", { n: 2, days });
      break;
    case RecurrenceType.EveryThirdWeek:
      pattern = t("sessionForm.summary.everyNWeeks", { n: 3, days });
      break;
    case RecurrenceType.EveryFourthWeek:
      pattern = t("sessionForm.summary.everyNWeeks", { n: 4, days });
      break;
    case RecurrenceType.MonthlyDayNumber:
      pattern = t("sessionForm.summary.monthlyDay", {
        day: input.recurrenceDaysNumber ?? "?",
      });
      break;
    case RecurrenceType.MonthlyFirstDay:
      pattern = t("sessionForm.summary.monthlyFirst", {
        day: dayLabel(t, input.recurrenceDays?.[0] ?? "MON"),
      });
      break;
    default:
      return null;
  }

  let end: string | null = null;
  if (input.recurrenceDateEnd) {
    end = t("sessionForm.summary.untilDate", {
      date: new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
        input.recurrenceDateEnd
      ),
    });
  } else if (input.finishAfterOccurrences) {
    end = t("sessionForm.summary.afterCount", {
      count: input.finishAfterOccurrences,
    });
  }

  return end ? `${pattern} · ${end}` : pattern;
}
